<template>
  <div class="skill-page">
    <!-- ===== 头部 ===== -->
    <div class="header">
      <h1>🔗 Vue2 事件总线</h1>
      <p>EventBus —— 跨组件通信的经典模式</p>
    </div>

    <div class="container">
      <!-- ===== 说明 ===== -->
      <div class="intro">
        <h2>📖 什么是事件总线？</h2>
        <p>
          <strong>事件总线（EventBus）</strong> 是 Vue 2 中一种轻量级跨组件通信方案。
          通过创建一个空的 Vue 实例作为中央事件通道，任意组件都可以
          <code>$emit</code> 触发事件，其他组件通过 <code>$on</code> 监听并响应，
          实现兄弟/跨级组件之间的解耦通信。<br/>
          ⚠️ <strong>注意：</strong> 组件销毁前务必调用 <code>$off</code> 取消订阅，防止内存泄漏。
        </p>
      </div>

      <!-- ===== 主体：发送 + 接收 ===== -->
      <div class="row">
        <!-- ===== 发送组件 ===== -->
        <div class="col">
          <div class="card">
            <h3>
              📤 消息发送器
              <span class="badge emit">$emit</span>
            </h3>
            <div class="form-group">
              <label>事件名称</label>
              <select v-model="form.eventName">
                <option value="message:info">message:info</option>
                <option value="message:warning">message:warning</option>
                <option value="message:error">message:error</option>
                <option value="user:login">user:login</option>
                <option value="user:logout">user:logout</option>
              </select>
            </div>
            <div class="form-group">
              <label>消息内容</label>
              <input v-model="form.content" placeholder="输入要发送的消息..." @keyup.enter="send" />
            </div>
            <button class="btn btn-primary" @click="send" :disabled="!form.content.trim()">
              🚀 发送消息
            </button>
          </div>

          <!-- ===== 订阅管理 ===== -->
          <div class="card">
            <h3>
              📋 订阅管理
              <span class="badge on">$on</span>
              <span class="badge off">$off</span>
            </h3>
            <div class="sub-item">
              <span class="sub-name">message:*</span>
              <span>
                <span class="sub-status" :class="{ inactive: !subs.message }">
                  {{ subs.message ? '已订阅' : '未订阅' }}
                </span>
                <button class="btn btn-sm btn-success" @click="subscribeMessage" :disabled="subs.message">订阅</button>
                <button class="btn btn-sm btn-danger" @click="unsubscribeMessage" :disabled="!subs.message">取消</button>
              </span>
            </div>
            <div class="sub-item">
              <span class="sub-name">user:*</span>
              <span>
                <span class="sub-status" :class="{ inactive: !subs.user }">
                  {{ subs.user ? '已订阅' : '未订阅' }}
                </span>
                <button class="btn btn-sm btn-success" @click="subscribeUser" :disabled="subs.user">订阅</button>
                <button class="btn btn-sm btn-danger" @click="unsubscribeUser" :disabled="!subs.user">取消</button>
              </span>
            </div>
          </div>
        </div>

        <!-- ===== 接收组件 ===== -->
        <div class="col">
          <div class="card">
            <h3>
              📥 消息接收器
              <span class="badge on">$on</span>
            </h3>
            <div class="toolbar">
              <button class="btn btn-sm btn-danger" @click="clearMessages">清空</button>
            </div>
            <ul class="msg-list" v-if="messages.length">
              <li v-for="(msg, i) in messages" :key="i">
                <span class="msg-tag" :class="msg.level">{{ msg.eventName }}</span>
                <span>{{ msg.content }}</span>
                <span class="msg-time">{{ msg.time }}</span>
              </li>
            </ul>
            <div class="msg-empty" v-else>
              📭 暂无消息，请在上方发送一条消息
            </div>
          </div>

          <!-- ===== 日志 ===== -->
          <div class="card">
            <h3>
              📜 事件日志
              <span class="badge log">Log</span>
            </h3>
            <div class="toolbar">
              <button class="btn btn-sm btn-danger" @click="clearLogs">清空</button>
            </div>
            <ul class="log-list" ref="logList">
              <li v-for="(log, i) in logs" :key="i">
                <span class="log-time">[{{ log.time }}]</span>
                <span :class="log.type === 'emit' ? 'log-emit' : 'log-on'">
                  {{ log.text }}
                </span>
              </li>
              <li v-if="!logs.length" class="log-wait">等待事件触发...</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- ===== 代码展示 ===== -->
      <div class="card code-card">
        <h3>💡 核心代码</h3>
        <pre class="code-pre">
// 1. 创建 EventBus（在 main.js 或单独文件）
Vue.prototype.$bus = new Vue()

// 2. 发送消息（任意组件中）
this.$bus.$emit('message:info', { content: 'Hello', level: 'info' })

// 3. 接收消息（任意组件中，created 中订阅）
this.$bus.$on('message:info', (data) => { /* 处理 */ })

// 4. 取消订阅（beforeDestroy 中清理，防止内存泄漏）
this.$bus.$off('message:info')</pre>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'

// 创建事件总线（模块级单例）：所有组件实例共享此通道
const bus = new Vue()

function now() {
  const d = new Date()
  return (
    String(d.getHours()).padStart(2, '0') + ':' +
    String(d.getMinutes()).padStart(2, '0') + ':' +
    String(d.getSeconds()).padStart(2, '0')
  )
}

export default {
  name: 'EventBusDemo',
  data() {
    return {
      form: {
        eventName: 'message:info',
        content: ''
      },
      messages: [],
      logs: [],
      subs: {
        message: false,
        user: false
      }
    }
  },

  created() {
    // 启动时默认订阅所有消息
    this.subscribeMessage()
    this.subscribeUser()
  },

  beforeDestroy() {
    // 清理所有订阅，防止内存泄漏
    bus.$off('message:info')
    bus.$off('message:warning')
    bus.$off('message:error')
    bus.$off('user:login')
    bus.$off('user:logout')
  },

  methods: {
    /* ---- 发送消息 ---- */
    send() {
      if (!this.form.content.trim()) return
      const eventName = this.form.eventName
      const parts = eventName.split(':')
      const level = parts.length > 1 ? parts[1] : 'info'
      const payload = {
        content: this.form.content.trim(),
        level: level,
        timestamp: Date.now()
      }

      // 通过事件总线发送
      bus.$emit(eventName, payload)
      this.addLog('emit', '$emit → ' + eventName + ' : "' + payload.content + '"')
    },

    /* ---- 订阅 message:* ---- */
    subscribeMessage() {
      const self = this
      bus.$on('message:info', function(data) { self.onMessage('message:info', data) })
      bus.$on('message:warning', function(data) { self.onMessage('message:warning', data) })
      bus.$on('message:error', function(data) { self.onMessage('message:error', data) })
      this.subs.message = true
      this.addLog('on', '$on 订阅 message:*')
    },

    /* ---- 取消订阅 message:* ---- */
    unsubscribeMessage() {
      bus.$off('message:info')
      bus.$off('message:warning')
      bus.$off('message:error')
      this.subs.message = false
      this.addLog('on', '$off 取消 message:*')
    },

    /* ---- 订阅 user:* ---- */
    subscribeUser() {
      const self = this
      bus.$on('user:login', function(data) { self.onMessage('user:login', data) })
      bus.$on('user:logout', function(data) { self.onMessage('user:logout', data) })
      this.subs.user = true
      this.addLog('on', '$on 订阅 user:*')
    },

    /* ---- 取消订阅 user:* ---- */
    unsubscribeUser() {
      bus.$off('user:login')
      bus.$off('user:logout')
      this.subs.user = false
      this.addLog('on', '$off 取消 user:*')
    },

    /* ---- 收到消息回调 ---- */
    onMessage(eventName, data) {
      this.messages.unshift({
        eventName: eventName,
        content: data.content,
        level: data.level,
        time: now()
      })
      this.addLog('on', '$on 收到 ' + eventName + ' : "' + data.content + '"')

      // 自动滚动到日志底部
      const self = this
      self.$nextTick(function() {
        const el = self.$refs.logList
        if (el) el.scrollTop = el.scrollHeight
      })
    },

    /* ---- 清空消息 ---- */
    clearMessages() {
      this.messages = []
    },

    /* ---- 清空日志 ---- */
    clearLogs() {
      this.logs = []
    },

    /* ---- 添加日志 ---- */
    addLog(type, text) {
      this.logs.push({ type: type, text: text, time: now() })
    }
  }
}
</script>

<style scoped>
.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: 32px 20px;
  text-align: center;
  border-radius: 12px;
  margin-bottom: 24px;
}

.header h1 {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: 2px;
  margin: 0;
}

.header p {
  margin-top: 10px;
  font-size: 15px;
  opacity: 0.85;
}

.container {
  max-width: 900px;
  margin: 0 auto;
}

.intro {
  background: var(--surface-1);
  border-radius: 12px;
  padding: 24px 28px;
  margin-bottom: 28px;
  box-shadow: var(--shadow-surface-1);
  border-left: 4px solid #667eea;
}

.intro h2 {
  font-size: 20px;
  margin-bottom: 10px;
  color: #667eea;
}

.intro p {
  font-size: 14px;
  line-height: 1.7;
  color: #555;
}

.intro code {
  background: var(--surface-2);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  color: #e83e8c;
}

.card {
  background: var(--surface-1);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: var(--shadow-surface-1);
}

.card h3 {
  font-size: 18px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.card h3 .badge {
  display: inline-block;
  font-size: 12px;
  padding: 2px 10px;
  border-radius: 20px;
  color: #fff;
  font-weight: 600;
}

.badge.emit { background: #28a745; }
.badge.on { background: #007bff; }
.badge.log { background: #6c757d; }
.badge.off { background: #dc3545; }

.row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.col {
  flex: 1;
  min-width: 260px;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
}

.form-group input,
.form-group select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid rgba(31, 45, 61, 0.06);
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

.btn {
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.15s;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
}

.btn-primary { background: #667eea; color: #fff; }
.btn-success { background: #28a745; color: #fff; }
.btn-danger { background: #dc3545; color: #fff; }
.btn-sm { padding: 6px 14px; font-size: 13px; }

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
}

.msg-list {
  list-style: none;
  max-height: 260px;
  overflow-y: auto;
  padding: 0;
  margin: 0;
}

.msg-list li {
  padding: 10px 14px;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
  font-size: 14px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.msg-list li:last-child {
  border-bottom: none;
}

.msg-tag {
  display: inline-block;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  color: #fff;
  margin-top: 1px;
}

.msg-tag.info { background: #17a2b8; }
.msg-tag.warning { background: #ffc107; color: #333; }
.msg-tag.error { background: #dc3545; }
.msg-tag.success { background: #28a745; }

.msg-time {
  font-size: 12px;
  color: #aaa;
  margin-left: auto;
  flex-shrink: 0;
}

.msg-empty {
  color: #aaa;
  text-align: center;
  padding: 30px 0;
  font-size: 14px;
}

.log-list {
  list-style: none;
  max-height: 200px;
  overflow-y: auto;
  background: #1e1e2e;
  border-radius: 8px;
  padding: 12px 16px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  margin: 0;
}

.log-list li {
  color: #a0e9a0;
  padding: 4px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  word-break: break-all;
}

.log-list li:last-child {
  border-bottom: none;
}

.log-list .log-time { color: #888; }
.log-list .log-emit { color: #7ec8e3; font-weight: 600; }
.log-list .log-on { color: #f9c95c; }

.log-wait {
  color: #666;
  text-align: center;
}

.sub-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(31, 45, 61, 0.06);
}

.sub-item:last-child {
  border-bottom: none;
}

.sub-item .sub-name {
  font-weight: 600;
  font-size: 14px;
  color: #333;
}

.sub-item .sub-status {
  font-size: 12px;
  color: #28a745;
}

.sub-item .sub-status.inactive {
  color: #dc3545;
}

.toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 10px;
}

.code-card {
  margin-top: 8px;
}

.code-pre {
  background: var(--surface-1);
  padding: 16px;
  border-radius: 8px;
  font-size: 13px;
  overflow-x: auto;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-all;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
}
</style>
